/**
 * GrudgeMatch — Head-to-Head Rivalry History (v2 redesign)
 *
 * Rival cards: avatar, name (+ THIS WEEK tag on your next opponent),
 * JAI's one-line rivalry verdict, and a mini stat strip
 * (all-time record / streak / avg point diff). Memorable moment below.
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolNav from '../components/ToolNav'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, BlueTag } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const PREVIEW_OPPONENTS = [
  {
    name: 'Dunks & Dimes',
    wins: 8, losses: 3,
    avgScoreMe: 184.6, avgScoreThem: 171.2,
    streak: 'W3',
    thisWeek: true,
    jaiVerdict: "Your nemesis on paper, your punching bag in practice. They've never solved your rebounding dominance.",
    rivalry: 'Dominated',
    moment: { week: 9, year: 2024, result: 'W', score: '231.4 – 168.2', note: 'Highest ever score in this matchup. Tatum went nuclear.' },
  },
  {
    name: 'Hoop Schemers',
    wins: 5, losses: 6,
    avgScoreMe: 178.3, avgScoreThem: 180.1,
    streak: 'L1',
    jaiVerdict: "The only manager in this league who consistently out-schemes you. Every matchup is a coin flip — you've lost more often than you've won.",
    rivalry: 'Rivalry',
    moment: { week: 6, year: 2025, result: 'L', score: '172.4 – 174.8', note: 'Lost by 2.4 pts. Had Curry on bench who scored 38.' },
  },
  {
    name: 'Playoff or Bust',
    wins: 9, losses: 2,
    avgScoreMe: 188.4, avgScoreThem: 162.8,
    streak: 'W5',
    jaiVerdict: "You've broken their playoff dreams twice. They've given up making it competitive against you.",
    rivalry: 'Dominated',
    moment: { week: 12, year: 2024, result: 'W', score: '209.1 – 141.2', note: 'Biggest margin ever. Their lineup had 3 players on back-to-back rest.' },
  },
  {
    name: 'The Benchmarks',
    wins: 4, losses: 7,
    avgScoreMe: 170.1, avgScoreThem: 179.6,
    streak: 'L2',
    jaiVerdict: "They have your number. You consistently underperform your season average against this team — something about their roster matchup neutralises your strengths.",
    rivalry: 'Nemesis',
    moment: { week: 3, year: 2025, result: 'L', score: '161.2 – 192.4', note: 'Worst start/sit week of the season — benched 3 players who combined for 110 pts.' },
  },
  {
    name: 'Fast Break FC',
    wins: 6, losses: 5,
    avgScoreMe: 181.0, avgScoreThem: 178.2,
    streak: 'W2',
    jaiVerdict: "A narrow overall edge. These games are always close — average margin of victory is just 8.4 points.",
    rivalry: 'Contested',
    moment: { week: 7, year: 2024, result: 'W', score: '182.4 – 182.0', note: 'Won by 0.4 points. Closest game in league history.' },
  },
  {
    name: 'Dunk Dynasty',
    wins: 7, losses: 4,
    avgScoreMe: 185.2, avgScoreThem: 174.0,
    streak: 'W1',
    jaiVerdict: "You have a comfortable edge but they're improving. Their recent acquisitions have made these games much closer.",
    rivalry: 'Edge',
    moment: { week: 2, year: 2025, result: 'W', score: '196.0 – 188.4', note: 'Best game this season. Both teams fielded full rosters.' },
  },
]

const RIVALRY_COLORS = {
  Dominated: V2.success,
  Edge:      V2.success,
  Contested: V2.blue,
  Rivalry:   V2.warning,
  Nemesis:   V2.error,
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function GrudgeMatch() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? { name: 'The Hardwood Cartel' } : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  // Sort by win rate desc, but pin this week's opponent to the top
  const sorted = [...PREVIEW_OPPONENTS].sort((a, b) =>
    (b.thisWeek ? 1 : 0) - (a.thisWeek ? 1 : 0) ||
    (b.wins / (b.wins + b.losses)) - (a.wins / (a.wins + a.losses))
  )

  return (
    <AppShell>
      <V2Page
        topBar={<ToolNav leagueId={id} />}
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · All-time head-to-head` : 'All-time head-to-head'}
          </Link>
        }
        title="Grudge Match"
        sub="Your history against every manager in the league."
        maxWidth={800}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sorted.map(opp => {
            const diff = opp.avgScoreMe - opp.avgScoreThem
            const diffLabel = `${diff >= 0 ? '+' : '−'}${Math.abs(diff).toFixed(1)}`
            const rivalryColor = RIVALRY_COLORS[opp.rivalry] || V2.blue
            const cells = [
              [`${opp.wins}-${opp.losses}`, 'ALL-TIME', opp.wins >= opp.losses],
              [opp.streak, 'STREAK', opp.streak.startsWith('W')],
              [diffLabel, 'PT DIFF', diff >= 0],
            ]
            return (
              <V2Card key={opp.name} pad="16px 20px" style={opp.thisWeek ? { border: '1px solid rgba(30,91,214,0.3)', boxShadow: V2.emphasisShadow } : {}}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15.5, fontWeight: 800, color: V2.navy }}>{opp.name}</span>
                      {opp.thisWeek && <BlueTag>This week</BlueTag>}
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: rivalryColor, fontFamily: JAI_FONTS.mono }}>
                        {opp.rivalry}
                      </span>
                    </div>
                    <div style={{ fontSize: 15, color: V2.navy, marginTop: 4, lineHeight: 1.5 }}>{opp.jaiVerdict}</div>
                  </div>

                  {/* Mini stat strip */}
                  <div style={{ display: 'flex', gap: 1, borderRadius: 10, overflow: 'hidden', border: `1px solid ${V2.hairSoft}`, flexShrink: 0 }}>
                    {cells.map(([v, l, good], i) => (
                      <div key={i} style={{ padding: '8px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 800, fontFamily: JAI_FONTS.mono, letterSpacing: '-0.02em', lineHeight: 1, color: good ? V2.navy : V2.error }}>
                          {v}
                        </div>
                        <div style={{ fontSize: 8.5, letterSpacing: '0.12em', color: V2.faint, fontFamily: JAI_FONTS.mono, marginTop: 4 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Memorable moment */}
                {opp.moment && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 12, paddingTop: 11, borderTop: '1px solid rgba(10,31,68,0.05)' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: opp.moment.result === 'W' ? V2.success : V2.error, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>
                      {opp.moment.result} · WK {opp.moment.week} {opp.moment.year}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: V2.navy, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>{opp.moment.score}</span>
                    <span style={{ fontSize: 12, color: V2.mute, lineHeight: 1.4 }}>{opp.moment.note}</span>
                  </div>
                )}
              </V2Card>
            )
          })}
        </div>
      </V2Page>
    </AppShell>
  )
}
